import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Box, Package, Save, User } from "lucide-react";
import { shipmentService } from "@/lib/database";
import type { Shipment } from "@/lib/supabase";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function EditShipment() {
  const { trackingNumber } = useParams();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [editedShipment, setEditedShipment] = useState<Shipment | null>(null);
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    loadShipment();
  }, []);

  useEffect(() => {
    setEditedShipment(shipment);
  }, [shipment]);

  const handleSave = async () => {
    if (!editedShipment) return;

    try {
      setSaving(true);
      delete editedShipment.customer;
      delete editedShipment.packages;

      // Update shipment details
      await shipmentService.update(editedShipment.id, editedShipment);

      // If status was changed, create a tracking event
      // TODO: Check if new status is forward or backward in the process
      // If forward, send email notification
      // Editin shipment is disabled for now, so this is moot
      if (shipment && editedShipment.status !== shipment.status) {
        await shipmentService.updateStatus(
          editedShipment.id,
          editedShipment.status!,
          "Admin Dashboard",
          `Status changed from "${shipment.status?.replace(
            new RegExp("_", "g"),
            " "
          )}" to "${editedShipment.status?.replace(
            new RegExp("_", "g"),
            " "
          )}"`,
          localStorage.getItem("userId") || undefined
        );
      }

      toast.success("Shipment updated successfully", {
        position: "top-center",
      });
    } catch (error) {
      console.error("Error updating shipment:", error);
      alert("Error updating shipment. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof Shipment, value: string | number) => {
    if (!editedShipment) return;
    setEditedShipment((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const calculateCubicFeet = (dimensions?: string): string => {
    if (!dimensions) return "0";

    const parts = dimensions.split("x").map((d) => parseFloat(d.trim()));
    if (parts.length !== 3 || parts.some(isNaN)) return "0";

    const [length, width, height] = parts;
    const cubicFeet = (length * width * height) / 1728; // Convert cubic inches to cubic feet
    return cubicFeet.toFixed(2);
  };

  const loadShipment = async () => {
    try {
      setLoading(true);
      const data = await shipmentService.getByTrackingNumber(trackingNumber!);

      setShipment(data);
    } catch (error) {
      console.error("Error loading shipment:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading shipment details...</div>
      </div>
    );
  }

  if (!shipment || !editedShipment) return null;

  const packageItems =
    shipment.packages?.flatMap((pkg) => pkg.items || []) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Edit Shipment</h1>
            <p className="text-gray-600">
              Tracking Number: {shipment.tracking_number}
            </p>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#254164] hover:bg-[#1a2d47]"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Shipment Information</CardTitle>
              <CardDescription>
                Basic shipment details and status
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tracking Number</Label>
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-gray-400" />
                    <span className="font-mono">
                      {shipment.tracking_number}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>

                  <Select
                    value={editedShipment.status}
                    onValueChange={(value: Shipment["status"] | string) =>
                      handleInputChange("status", value!)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created">Created</SelectItem>
                      <SelectItem value="estimated">Estimated</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>

                      <SelectItem value="picking_up_from_sender">
                        Picking Up From Sender
                      </SelectItem>
                      <SelectItem value="received_at_warehouse">
                        Received at Warehouse
                      </SelectItem>
                      <SelectItem value="processing_at_warehouse">
                        Processing at Warehouse
                      </SelectItem>

                      <SelectItem value="in_transit_to_country">
                        In Transit to Country
                      </SelectItem>
                      <SelectItem value="arrived_at_warehouse">
                        Arrived at Warehouse
                      </SelectItem>
                      <SelectItem value="ready_for_pickup">
                        Ready for Pickup
                      </SelectItem>

                      <SelectItem value="in_transit_to_recipient">
                        In Transit to Recipient
                      </SelectItem>
                      <SelectItem value="delivered_to_recipient">
                        Delivered to Recipient
                      </SelectItem>
                      <SelectItem value="picked_up_by_recipient">
                        Picked Up by Recipient
                      </SelectItem>

                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 flex flex-col">
                  <Label>Shipping Method</Label>
                  <Select
                    value={editedShipment.shipping_method}
                    onValueChange={(value: Shipment["shipping_method"]) =>
                      handleInputChange("shipping_method", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="air">Air Shipping</SelectItem>
                      <SelectItem value="sea">Sea Shipping</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 flex flex-col">
                  <Label>
                    {editedShipment.shipping_method === "sea"
                      ? "Volume"
                      : "Weight"}
                  </Label>

                  {editedShipment.shipping_method === "sea" ? (
                    <span className="flex flex-col">
                      <Input
                        type="text"
                        value={editedShipment.dimensions}
                        placeholder="e.g.: 12 x 10 x 8"
                        onChange={(e) =>
                          handleInputChange("dimensions", e.target.value)
                        }
                      />

                      <span className="text-sm text-gray-500">
                        Length x Width x Height (inches) ={" "}
                        {calculateCubicFeet(editedShipment.dimensions)} cu ft
                      </span>
                    </span>
                  ) : (
                    <span className="flex items-center space-x-2">
                      <Input
                        type="number"
                        value={editedShipment.weight}
                        onChange={(e) =>
                          handleInputChange(
                            "weight",
                            parseFloat(e.target.value)
                          )
                        }
                      />
                      <span className="text-sm text-gray-500">lbs</span>
                    </span>
                  )}
                </div>
              </div>

              {/* <div className="space-y-2">
                  <Label>Package Contents</Label>
                  <p className="text-sm">{shipment.package_contents}</p>
                </div> */}

              {editedShipment.special_instructions && (
                <div className="space-y-2">
                  <Label>Special Instructions</Label>

                  <Textarea
                    value={editedShipment.special_instructions}
                    onChange={(e) =>
                      handleInputChange("special_instructions", e.target.value)
                    }
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
              <CardDescription>Details about the customer</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-gray-400" />
                <span>
                  {shipment.customer?.first_name} {shipment.customer?.last_name}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {shipment.customer?.email}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Shipment Items */}
          {packageItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Box className="h-4 w-4 mr-2" />
                  Package Items ({packageItems.length})
                </CardTitle>
                <CardDescription>
                  Individual items in this shipment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {packageItems.map((item) => (
                    <div key={item.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {item.item_name}
                          </p>
                          {item.item_description && (
                            <p className="text-sm text-gray-600">
                              {item.item_description}
                            </p>
                          )}
                          {item.category && (
                            <p className="text-xs text-gray-500">
                              Category: {item.category.name}
                            </p>
                          )}
                        </div>
                        <div className="text-right text-sm text-gray-600">
                          <p>Qty: {item.quantity}</p>
                          {item.total_weight && (
                            <p>Weight: {item.total_weight} lbs</p>
                          )}
                          {/* {item.value && <p>Value: ${item.value}</p>} */}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pricing Information */}
          <Card>
            <CardHeader>
              <CardTitle>Pricing Information</CardTitle>
              <CardDescription>Cost breakdown and fees</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 flex flex-col">
                  <Label>Delivery Fee</Label>

                  <span className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">$</span>

                    <Input
                      type="text"
                      value={editedShipment.delivery_fee}
                      onChange={(e) =>
                        handleInputChange(
                          "delivery_fee",
                          parseFloat(e.target.value) || 0
                        )
                      }
                    />
                  </span>
                </div>

                <div className="space-y-2 flex flex-col">
                  <Label>Extra Charge</Label>

                  <span className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">$</span>

                    <Input
                      type="text"
                      value={editedShipment.extra_charge || 0}
                      onChange={(e) =>
                        handleInputChange(
                          "extra_charge",
                          parseFloat(e.target.value) || 0
                        )
                      }
                    />
                  </span>
                </div>
              </div>

              <div className="pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold">Total Cost</Label>
                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-bold text-green-600">
                      ${shipment.total_cost}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle>Important Dates</CardTitle>
              <CardDescription>Key shipment dates and timeline</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Created:</span>
                  <span>
                    {new Date(shipment.created_at!).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Last Updated:</span>
                  <span>
                    {new Date(shipment.updated_at!).toLocaleDateString()}
                  </span>
                </div>
                {shipment.estimated_delivery_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Estimated Delivery:</span>
                    <span>
                      {new Date(
                        shipment.estimated_delivery_date
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {shipment.actual_delivery_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Actual Delivery:</span>
                    <span>
                      {new Date(
                        shipment.actual_delivery_date
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
